import React, { useEffect } from 'react';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import '@patternfly/react-core/dist/styles/base.css';

import './App.css';

import Layout from './Layout';
import routes from './routes';

import CustomizationPage from './pages/customization/CustomizationPage';
import GlobalRoisPage from './pages/global-rois/GlobalRoisPage';
import TextEditorPage from './pages/text-editor/TextEditorPage';
import LicensePage from './pages/license/LicensePage';
import AboutPage from './pages/about/AboutPage';
import ModelSelectionPage from './pages/selection/SelectionRulePage';
import DashboardPage from './pages/dashboard/DashboardPage';
import { getAppDeploymentInfo, selectAnyUnsavedChangesInApp } from './store/selectors';
import LoggedOutPage from './pages/logged-out/LoggedOutPage';
import { LOGGED_OUT_URI } from './environments';
import AppWrapper from './pages/auth/AppWrapper';
import HomePage from './pages/home/HomePage';
import DaemonConfigPage from './pages/daemon/DaemonConfigPage';

const loader = document.querySelector('.loader');
const hideLoader = () => loader?.classList.add('loader-hide');

function App() {

  // hide the splash screen once the app has loaded
  useEffect(hideLoader, []);

  /** Prompt user if they're trying to close the app with unsaved changes. */
  const anyUnsavedChanges = useSelector(selectAnyUnsavedChangesInApp);
  useEffect(() => {
    if (anyUnsavedChanges) {
      window.onbeforeunload = function () {
        // Note that modern browsers don't actually support showing these messages, but returning 
        // a string here makes them show their default "you have unsaved changes" dialog.
        return "You have unsaved changes. Are you sure you want to leave?";
      };
    } else {
      // remove any beforeunload prompt
      window.onbeforeunload = null;
    }
  }, [anyUnsavedChanges]);

  const deploymentInfo = useSelector(getAppDeploymentInfo);

  return (
    <Router>
      <Switch>
        <Route exact path={LOGGED_OUT_URI}>
          <LoggedOutPage />
        </Route>
        <Route path={'/'}>
          <AppWrapper>
            <Layout>
              <Switch>
                <Route path={routes.customizationPage.slug}>
                  <CustomizationPage />
                </Route>
                <Route path={routes.allStructuresPage.slug}>
                  <GlobalRoisPage />
                </Route>
                <Route path={routes.modelSelectionPage.slug}>
                  <ModelSelectionPage />
                </Route>
                {deploymentInfo && !deploymentInfo.disableDaemonConfigPage && (
                  <Route path={routes.daemonConfigPage.slug}>
                    <DaemonConfigPage />
                  </Route>
                )}
                <Route path={routes.dashboardPage.slug}>
                  <DashboardPage />
                </Route>
                <Route path={routes.textEditorPage.slug}>
                  <TextEditorPage />
                </Route>
                <Route path={routes.licensePage.slug}>
                  <LicensePage />
                </Route>
                <Route path={routes.aboutPage.slug}>
                  <AboutPage />
                </Route>
                <Route path={routes.homePage.slug}>
                  <HomePage />
                </Route>
              </Switch>
            </Layout>
          </AppWrapper>
        </Route>
      </Switch>
    </Router>
  );
}

export default App;
